import * as React from 'react';

import { NO_OPTIONS, remember, resolving, seenOptions } from './filter-option-cache';
import type { FilterDef, FilterOption } from './types';

/**
 * Labels for the values applied to a filter: enum options, then options seen
 * in earlier pickers, then `resolveOptions` for anything still unknown.
 */
export function useResolvedOptions(
  def: FilterDef<never>,
  values: readonly string[],
): ReadonlyMap<string, FilterOption> {
  const resolve = def.type === 'async' ? def.resolveOptions : undefined;
  const known = seenOptions.get(def.key);
  const missing = React.useMemo(
    () => (resolve ? [...values].filter((value) => !known?.has(value)).sort() : []),
    [known, resolve, values],
  );
  const missingKey = missing.join('\u0000');
  const [resolved, setResolved] = React.useState<readonly FilterOption[]>(NO_OPTIONS);

  React.useEffect(() => {
    if (!resolve || missingKey === '') return;
    const requestKey = `${def.key}\u0000\u0000${missingKey}`;
    let request = resolving.get(requestKey);
    if (!request) {
      // Shared, so no one caller may abort it; each ignores a late answer.
      request = resolve(missingKey.split('\u0000'), new AbortController().signal);
      resolving.set(requestKey, request);
      request.then(
        (options) => remember(def.key, options),
        () => undefined,
      );
      request.finally(() => resolving.delete(requestKey)).catch(() => undefined);
    }
    let cancelled = false;
    request.then(
      (options) => {
        if (!cancelled) setResolved(options);
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [def.key, missingKey, resolve]);

  // Rebuilt when `values` change: `known` is filled in place as options load.
  return React.useMemo(() => {
    const map = new Map<string, FilterOption>();
    for (const value of values) {
      const option =
        (def.type === 'enum' ? def.options.find((o) => o.value === value) : undefined) ??
        resolved.find((o) => o.value === value) ??
        known?.get(value);
      if (option) map.set(value, option);
    }
    return map;
  }, [def, known, resolved, values]);
}
